import React, { useContext, useMemo, useState } from 'react';
import { CustomFieldContext } from '../../../../context/CustomFieldContext';
import { CustomField, CustomFieldUtil } from '../../../../types/custom_field';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { translate } from '../../../../types/translation/Translator';
import { CustomFieldPropertiesFilter } from '../../../../VentoryUI/components/filters/CustomFieldFilter';
import { CreateButton } from '../../../components/Button';
import Content from '../../../components/Content';
import { SearchFilter } from '../../../components/Filter';
import LoadingPane from '../../../components/Loading/LoadingPane';
import Table from '../../../components/Table';
import { DeleteIconButton } from '../../../icons';
import CustomFieldModal from './CustomFieldModal';
import DeleteCustomFieldModal from './DeleteCustomFieldModal';

interface ModalProps {
  open: boolean;
  customField: CustomField | undefined;
}

export default function CustomFieldSettingsScreen() {
  const { customFields, customFieldsLoading } = useContext(CustomFieldContext);

  const allItems = useMemo(() => {
    return [...customFields.values()];
  }, [customFields]);

  const [items, setItems] = useState(allItems);
  const [modalProps, setModalProps] = useState<ModalProps>({ open: false, customField: undefined });
  const [deleteCustomField, setDeleteCustomField] = useState<CustomField | undefined>(undefined);

  const filter = new CustomFieldPropertiesFilter();

  if (customFieldsLoading) return <LoadingPane />;

  return (
    <>
      {modalProps.open && (
        <CustomFieldModal
          open={modalProps.open}
          setOpen={open => setModalProps({ ...modalProps, open })}
          customField={modalProps.customField}
        />
      )}
      {deleteCustomField && (
        <DeleteCustomFieldModal
          open={true}
          setOpen={open => !open && setDeleteCustomField(undefined)}
          customField={deleteCustomField}
        />
      )}

      <Content>
        <div className='flex h-full flex-col gap-8'>
          <div className='grid gap-2'>
            <p className='page-title'>{translate({ key: TranslationKey.customFieldSettingsScreenTitle })}</p>
            <p className='subtitle'>{translate({ key: TranslationKey.customFieldSettingsScreenSubtitle })}</p>
          </div>
          <div className='flex flex-grow flex-col gap-4'>
            <div className='flex justify-between gap-2'>
              <SearchFilter filter={filter} items={allItems} setItems={setItems} />
              <CreateButton onClick={() => setModalProps({ open: true, customField: undefined })} />
            </div>
            <Table
              filter={filter}
              items={items}
              onClick={customField => setModalProps({ open: true, customField })}
              headers={[
                {
                  key: 'name',
                  name: translate({ key: TranslationKey.name }),
                  text: (item: CustomField) => item.name,
                },
                {
                  key: 'type',
                  name: translate({ key: TranslationKey.type }),
                  text: (item: CustomField) => CustomFieldUtil.customFieldTypeToString(item.type),
                },
                {
                  key: 'entityType',
                  name: translate({ key: TranslationKey.entityType }),
                  text: (item: CustomField) => CustomFieldUtil.customFieldEntityTypeToString(item.entityType),
                },
                {
                  key: 'entitySubType',
                  name: translate({ key: TranslationKey.entitySubtype }),
                  text: (item: CustomField) => CustomFieldUtil.customFieldEntitySubtypeToString(item.entitySubtype),
                },
                {
                  key: 'mandatory',
                  name: translate({ key: TranslationKey.mandatory }),
                  text: (item: CustomField) =>
                    translate({ key: item.mandatory ? TranslationKey.yes : TranslationKey.no }),
                },
                {
                  key: 'delete',
                  name: '',
                  text: item => <DeleteIconButton onClick={() => setDeleteCustomField(item)} />,
                },
              ]}
            />
          </div>
        </div>
      </Content>
    </>
  );
}
